import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Receipt, Plus, Trash2, Edit, FileSpreadsheet, FileText, RefreshCw,
  TrendingUp, TrendingDown, Scale, FileCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyForm = { type: 'indirilecek', date: new Date().toISOString().slice(0, 10), description: '', base_amount: '', rate: 20, notes: '' };

const VatTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [yearly, setYearly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = yearly ? { year } : { year, month };
      const res = await axios.get(`${API}/vat/summary`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KDV verisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [year, month, yearly]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, date: `${year}-${String(month).padStart(2, '0')}-01` }); setShowModal(true); };
  const openEdit = (rec) => {
    setEditing(rec);
    setForm({ type: rec.type, date: rec.date, description: rec.description, base_amount: rec.base_amount, rate: rec.rate, notes: rec.notes || '' });
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.description?.trim()) { toast.error('Açıklama zorunlu'); return; }
    if (!(Number(form.base_amount) > 0)) { toast.error('Matrah 0’dan büyük olmalı'); return; }
    setSaving(true);
    try {
      const body = { ...form, base_amount: Number(form.base_amount), rate: Number(form.rate) || 20 };
      if (editing) await axios.put(`${API}/vat-records/${editing.id}`, body);
      else await axios.post(`${API}/vat-records`, body);
      toast.success(editing ? 'KDV kaydı güncellendi' : 'KDV kaydı eklendi');
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`"${rec.description}" kaydını silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/vat-records/${rec.id}`);
      toast.success('KDV kaydı silindi');
      await load();
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const download = async (format) => {
    try {
      const params = yearly ? { year, format } : { year, month, format };
      const res = await axios.get(`${API}/vat/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kdv_raporu_${year}${yearly ? '' : '_' + String(month).padStart(2, '0')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Rapor indirildi');
    } catch (e) {
      toast.error('Rapor oluşturulamadı');
    }
  };

  const s = data?.summary;
  const previewVat = Math.round((Number(form.base_amount) || 0) * (Number(form.rate) || 0)) / 100;

  return (
    <div className="space-y-4" data-testid="vat-tab">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-800 text-white p-5">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-indigo-500/20 blur-3xl rounded-full" />
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-lg backdrop-blur"><Receipt className="w-6 h-6" /></div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">KDV Hesaplama</h2>
              <p className="text-xs text-indigo-200">Faturalı işlemlerden hesaplanan KDV, alış KDV’si ve ödenecek tutar</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-28 bg-white/10 border-white/30 text-white" data-testid="vat-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map(i => now.getFullYear() - i).map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearly ? 'all' : String(month)} onValueChange={(v) => { if (v === 'all') { setYearly(true); } else { setYearly(false); setMonth(Number(v)); } }}>
              <SelectTrigger className="h-9 w-36 bg-white/10 border-white/30 text-white" data-testid="vat-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Yıl</SelectItem>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} className="h-9 bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="vat-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Yenile
            </Button>
            <Button onClick={() => download('excel')} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="vat-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button onClick={() => download('pdf')} className="h-9 bg-rose-600 hover:bg-rose-700" data-testid="vat-pdf-btn">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button onClick={openNew} className="h-9 bg-white text-indigo-900 hover:bg-indigo-50" data-testid="vat-add-btn">
              <Plus className="w-4 h-4 mr-1" /> KDV Kaydı
            </Button>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-emerald-500" data-testid="vat-kpi-hesaplanan">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Hesaplanan KDV</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{loading ? '...' : tl(s?.hesaplanan)}</p>
                <p className="text-xs text-slate-400 mt-1">Satış / faturalı maçlar</p>
              </div>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500" data-testid="vat-kpi-indirilecek">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">İndirilecek KDV</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{loading ? '...' : tl(s?.indirilecek)}</p>
                <p className="text-xs text-slate-400 mt-1">Alış faturaları (manuel)</p>
              </div>
              <TrendingDown className="w-5 h-5 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-rose-500" data-testid="vat-kpi-odenecek">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Ödenecek KDV</p>
                <p className={`text-2xl font-bold mt-1 ${(s?.odenecek || 0) >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {loading ? '...' : tl(s?.odenecek)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Hesaplanan − İndirilecek</p>
              </div>
              <Scale className="w-5 h-5 text-rose-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500" data-testid="vat-kpi-matrah">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Faturalı Matrah</p>
                <p className="text-2xl font-bold text-indigo-700 mt-1">{loading ? '...' : tl(s?.auto_base)}</p>
                <p className="text-xs text-slate-400 mt-1">{s?.invoiced_count || 0} faturalı maç</p>
              </div>
              <FileCheck className="w-5 h-5 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Aylık kırılım */}
      <Card>
        <CardHeader className="py-3 border-b"><CardTitle className="text-base">Aylık Kırılım</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="vat-monthly-table">
            <thead className="bg-slate-800 text-white">
              <tr>
                <th className="text-left px-3 py-2">Ay</th>
                <th className="text-right px-3 py-2">Hesaplanan</th>
                <th className="text-right px-3 py-2">İndirilecek</th>
                <th className="text-right px-3 py-2">Ödenecek</th>
                <th className="text-right px-3 py-2">Fatura</th>
              </tr>
            </thead>
            <tbody>
              {(data?.monthly || []).length === 0 ? (
                <tr><td colSpan={5} className="text-center py-6 text-slate-400">{loading ? 'Yükleniyor...' : 'Bu dönemde KDV hareketi yok'}</td></tr>
              ) : data.monthly.map((m, i) => (
                <tr key={m.month} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-3 py-2 font-medium">{m.month}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{tl(m.hesaplanan)}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{tl(m.indirilecek)}</td>
                  <td className="px-3 py-2 text-right font-bold">{tl(m.hesaplanan - m.indirilecek)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{m.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Faturalı rezervasyonlar */}
        <Card>
          <CardHeader className="py-3 border-b bg-emerald-50/50">
            <CardTitle className="text-base text-emerald-900">Faturalı Rezervasyonlar (otomatik)</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="vat-invoiced-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-2">Tarih</th>
                  <th className="text-left px-2 py-2">Müşteri</th>
                  <th className="text-right px-2 py-2">Matrah</th>
                  <th className="text-right px-2 py-2">KDV</th>
                  <th className="text-right px-2 py-2">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {(data?.invoiced_reservations || []).length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-400">{loading ? 'Yükleniyor...' : 'Faturalı rezervasyon yok'}</td></tr>
                ) : data.invoiced_reservations.map((r, i) => (
                  <tr key={r.id} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-2 py-1.5">{r.date}</td>
                    <td className="px-2 py-1.5 truncate max-w-[150px]">{r.customer_name}</td>
                    <td className="px-2 py-1.5 text-right">{tl(r.base_amount)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700 font-semibold">{tl(r.vat_amount)}</td>
                    <td className="px-2 py-1.5 text-right">{tl(r.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Manuel kayıtlar */}
        <Card>
          <CardHeader className="py-3 border-b bg-amber-50/50">
            <CardTitle className="text-base text-amber-900">Manuel KDV Kayıtları</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="vat-records-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-2">Tarih</th>
                  <th className="text-left px-2 py-2">Tür</th>
                  <th className="text-left px-2 py-2">Açıklama</th>
                  <th className="text-right px-2 py-2">Matrah</th>
                  <th className="text-right px-2 py-2">KDV</th>
                  <th className="text-right px-2 py-2">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {(data?.records || []).length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400">{loading ? 'Yükleniyor...' : 'Manuel kayıt yok'}</td></tr>
                ) : data.records.map((r, i) => (
                  <tr key={r.id} className={i % 2 ? 'bg-slate-50' : 'bg-white'} data-testid={`vat-record-${r.id}`}>
                    <td className="px-2 py-1.5">{r.date}</td>
                    <td className="px-2 py-1.5">
                      <Badge className={r.type === 'indirilecek' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}>
                        {r.type === 'indirilecek' ? 'İndirilecek' : 'Hesaplanan'}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 truncate max-w-[160px]" title={r.description}>{r.description}</td>
                    <td className="px-2 py-1.5 text-right">{tl(r.base_amount)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{tl(r.vat_amount)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => openEdit(r)} data-testid={`vat-edit-${r.id}`}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => remove(r)} data-testid={`vat-delete-${r.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Ekle / Düzenle modalı */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="vat-record-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'KDV Kaydını Düzenle' : 'Yeni KDV Kaydı'}</DialogTitle>
            <DialogDescription>Alış faturası (indirilecek) veya ek satış KDV’si (hesaplanan) girin</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="vat-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="indirilecek">İndirilecek KDV (alış faturası)</SelectItem>
                  <SelectItem value="hesaplanan">Hesaplanan KDV (ek satış)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Tarih</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="vat-form-date" />
              </div>
              <div>
                <Label className="text-xs">Oran (%)</Label>
                <Input type="number" step="1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="vat-form-rate" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Açıklama</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Örn: Spor malzemesi alış faturası" data-testid="vat-form-description" />
            </div>
            <div>
              <Label className="text-xs">Matrah (KDV’siz tutar ₺)</Label>
              <Input type="number" step="0.01" value={form.base_amount} onChange={(e) => setForm({ ...form, base_amount: e.target.value })} data-testid="vat-form-base" />
              <p className="text-xs text-slate-500 mt-1">Hesaplanan KDV: <span className="font-bold text-slate-800">{tl(previewVat)}</span> · Toplam: {tl((Number(form.base_amount) || 0) + previewVat)}</p>
            </div>
            <div>
              <Label className="text-xs">Not (opsiyonel)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vat-form-notes" />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full bg-indigo-700 hover:bg-indigo-800" data-testid="vat-form-submit">
              {saving ? 'Kaydediliyor...' : (editing ? 'Güncelle' : 'Kaydet')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VatTab;
